import { SvgIcon } from '@material-ui/core';
import { IconType } from 'react-icons';

import { SidebarFolderItem } from './SidebarFolderItem';
import { SidebarListItem } from './SidebarListItem';

type IconElementType = IconType | typeof SvgIcon;

export interface RenderableNavItem {
  id: string;
  label: string;
  icon?: IconElementType;
  children?: RenderableNavItem[];
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  tooltipTitle?: React.ReactNode;
}

export interface NavItemListProps {
  items: RenderableNavItem[];
  folderOpenState?: 'closed' | 'auto' | ((item: RenderableNavItem) => boolean);
  nestedClassName?: string;
}

export interface NavItemRendererProps {
  item: RenderableNavItem;
  folderOpenState?: 'closed' | 'auto' | ((item: RenderableNavItem) => boolean);
  nestedClassName?: string;
}

export const NavItemRenderer = ({
  item,
  folderOpenState = 'auto',
  nestedClassName = 'py-1 pl-6',
}: NavItemRendererProps) => {
  const getInitialOpen = (): boolean => {
    if (folderOpenState === 'closed') return false;
    if (folderOpenState === 'auto') {
      return item.children?.some((child) => child.selected) ?? false;
    }
    return folderOpenState(item);
  };

  if (item.children?.length) {
    return (
      <SidebarFolderItem
        htmlId={item.id}
        label={item.label}
        IconElement={item.icon}
        isOpen={getInitialOpen()}
      >
        <div className={nestedClassName}>
          {item.children.map((child) => (
            <SidebarListItem
              key={child.id}
              htmlId={child.id}
              label={child.label}
              IconElement={child.icon}
              selected={child.selected}
              disabled={child.disabled}
              handleClick={child.onClick}
              tooltipTitle={child.tooltipTitle}
            />
          ))}
        </div>
      </SidebarFolderItem>
    );
  }

  return (
    <SidebarListItem
      htmlId={item.id}
      label={item.label}
      IconElement={item.icon}
      selected={item.selected}
      disabled={item.disabled}
      handleClick={item.onClick}
      tooltipTitle={item.tooltipTitle}
    />
  );
};

export const NavItemList = ({
  items,
  folderOpenState = 'auto',
  nestedClassName = 'py-1 pl-6',
}: NavItemListProps) => {
  if (!items?.length) return null;

  return (
    <>
      {items.map((item) => (
        <NavItemRenderer
          key={item.id}
          item={item}
          folderOpenState={folderOpenState}
          nestedClassName={nestedClassName}
        />
      ))}
    </>
  );
};
